import { useState, FC, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import RocketIcon from '@mui/icons-material/Rocket';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import HelpOutlineSharpIcon from '@mui/icons-material/HelpOutlineSharp';
import PermContactCalendarRoundedIcon from '@mui/icons-material/PermContactCalendarRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import styles from 'shared/app-drawer/AppDrawer.module.css';
import { RoutesType } from 'shared/types/routes/routers-type';
import React from 'react';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import GradeIcon from '@mui/icons-material/Grade';
import { AppDrawerSelector } from 'entities/app-drawer/selector';
import { getRequest } from 'shared/utils/fetch-api';
import { unreadMessagesPresenceSelector } from 'entities/unread-messages/selector';
import { fetchUnreadMessages } from 'entities/unread-messages/actions/fetch-unread-messages';
import MarkunreadIcon from '@mui/icons-material/Markunread';


export const AppDrawer: FC = () => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const location = useLocation();
    const appDrawerSelector = useAppSelector(AppDrawerSelector);
    const dispatch = useAppDispatch();
    const unreadMessagesPresence = useAppSelector(unreadMessagesPresenceSelector);
     useEffect(() => {
        dispatch(fetchUnreadMessages());
    }, [])


    const menuItems = [
        { text: 'Статистика', icon: <HomeIcon />, path: `${RoutesType.Statistics}`, newIcon: true, dev: false, textRout: 'statistics', visible: appDrawerSelector.Statistics, state: { textRout: 'statistics', text: 'Статистика' } },
        { text: 'Договоры страхования', icon: <TextSnippetIcon />, path: `${RoutesType.ContractsList}`, newIcon: false, dev: false, textRout: 'insurance-contracts', visible: appDrawerSelector.InsuranceContracts, state: { textRout: 'insurance-contracts', text: 'Договоры страхования' } },
        { text: 'Программы страхования', icon: <RocketIcon />, path: `${RoutesType.ProgramsList}`, newIcon: false, dev: false, textRout: 'insurance-programs', visible: appDrawerSelector.InsurancePrograms, state: { textRout: 'insurance-programs', text: 'Программы страхования' } },
        { text: 'Список застрахованных', icon: <FormatListBulletedIcon />, path: `${RoutesType.InsuredPersons}`, newIcon: false, dev: false, textRout: 'list-of-insured-persons', visible: appDrawerSelector.ListOfInsuredPersons, state: { textRout: 'list-of-insured-persons', text: 'Список застрахованных' } },
        { text: 'Заявки', icon: <MarkunreadIcon />, path: `${RoutesType.TasksList}`, newIcon: false, newInfo: true, dev: false, textRout: 'applications', visible: appDrawerSelector.Applications, state: { textRout: 'applications', text: 'Заявки' } },
        { text: 'Бонусная программа', icon: <CardGiftcardIcon />, path: `${RoutesType.Dev}`, newIcon: false, dev: false, textRout: 'bonus-program', visible: appDrawerSelector.BonusProgram, state: { textRout: 'bonus-program', text: 'Бонусная программа' } },
        { text: 'Страхование родственников', icon: <FamilyRestroomIcon />, path: `${RoutesType.Dev}`, newIcon: false, dev: false, textRout: 'insurance-of-relatives', visible: appDrawerSelector.InsuranceOfRelatives, state: { textRout: 'insurance-of-relatives', text: 'Страхование родственников' } },
        { text: 'Договоры без конверсии', icon: <TextSnippetIcon />, path: `${RoutesType.Projects}`, newIcon: false, dev: false, textRout: 'projects', visible: appDrawerSelector.Projects, state: { textRout: 'projects', text: 'Договоры без конверсии' } },
    ];

    const menuItemsDown = [
        { text: 'FAQ', icon: <HelpOutlineSharpIcon />, path: `${RoutesType.Dev}`, newIcon: false, dev: false, textRout: 'faq', visible: true, state: { textRout: 'faq', text: 'FAQ' } },
        { text: 'Контакты', icon: <PermContactCalendarRoundedIcon />, path: `${RoutesType.Contacts}`, newIcon: false, dev: false, textRout: 'contacts', visible: false, state: { textRout: 'contacts', text: 'Контакты' } },
        { text: 'Обратная связь', icon: <SupportAgentRoundedIcon />, path: `${RoutesType.Feedback}`, newIcon: false, dev: false, textRout: 'feedback', visible: true, state: { textRout: 'feedback', text: 'Обратная связь' } },
    ];

    useEffect(() => {
        const shouldCollapseMenu = location.pathname.startsWith('/insurance-contracts/case') && location.search.includes('id=');
        if (isLargeScreen && !shouldCollapseMenu) {
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [isLargeScreen, location]);

    const handleItemClick = (path: string, state?: any) => {
        if (path) {
            navigate(path, { state });
            if (!isLargeScreen) {
                setOpen(false);
            }
        }
    };

    const showMenuIcon = !isLargeScreen || (location.pathname.startsWith('/insurance-contracts/case') && location.search.includes('id='));

    const renderMenuItem = (item: any, index: number) => {
        const itemContent = (
            <ListItemButton
                onClick={() => !item.dev && handleItemClick(item.path, { textRout: item.textRout, name: item.text, visible: item.visible })}
                className={`${styles.menuItem} ${(location.pathname === item.path && location.state?.textRout === item.textRout) ? styles.selected : ''} ${item.dev ? styles.dev : ''}`}
            >
                <ListItemIcon className={`${styles.menuItemIcon} ${(location.pathname === item.path && location.state?.textRout === item.textRout) ? styles.selected : ''}`}>
                    {item.icon}
                    <>
                    {unreadMessagesPresence && item.text === 'Заявки' && (
                            <>
                                <Tooltip title="Непрочитанные сообщения" placement="top">
                                    <span className={styles.hasUnreadMessages}></span>
                                </Tooltip>
                            </>

                        )}
                    </>
                </ListItemIcon>
                {open &&
                    <>
                        <ListItemText
                            className={`${styles.menuItemText} ${(location.pathname === item.path &&
                                location.state?.textRout === item.textRout) ? styles.selected : ''}`}
                            primary={
                                <span>
                                    {item.text}
                                    {item.newIcon && <span className={styles.newIcon}>Новое</span>}
                                    {item.newInfo && <span className={styles.newIcon}>Чат</span>}

                                </span>

                            }
                        />
                        {/* {unreadMessagesPresence && item.text === 'Заявки' && (
                            <>
                                <Tooltip title="Непрочитанные сообщения" placement="top">
                                    <span className={styles.hasUnreadMessages}></span>
                                </Tooltip>
                            </>

                        )} */}
                    </>

                }
            </ListItemButton>
        );

        return !open ? (
            <Tooltip key={index} title={item.text} placement="top">
                {itemContent}
            </Tooltip>
        ) : (
            <React.Fragment key={index}>
                {itemContent}
            </React.Fragment>
        );
    };

    return (
        <>
            <Box className={`${styles.menu} ${open ? '' : styles.closed} ${(!isLargeScreen || showMenuIcon) && open ? styles.overlay : ''}`}>
                <List>
                    {menuItems.map((item, index) => (
                        item.visible && renderMenuItem(item, index))
                    )}


                </List>
                <Box className={styles.bottomMenu}>
                    <List>
                        {menuItemsDown.map((item, index) => renderMenuItem(item, index))}
                    </List>
                </Box>
            </Box>
            {open && showMenuIcon && (
                <>
                    {document.getElementById('backdrop-root') ? (
                        ReactDOM.createPortal(
                            <Box className={styles.backdrop} onClick={() => setOpen(false)} />,
                            document.getElementById('backdrop-root')!
                        )
                    ) : (
                        <div>Error: backdrop-root element not found</div>
                    )}
                </>
            )}
        </>
    );
};
